import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Grid,
  Box,
  Paper,
  FormLabel,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Stack } from "@mui/system";

const DataStoreRequestRunQueryInputOutputFields = ({
  inputValues,
}: {
  inputValues: any;
}) => {
  const { queryContentSingle } = useSelector(
    (state: any) => state.dataStoreReq
  );
  const { output } = queryContentSingle ?? {};

  const structuredInputs = useMemo(() => {
    if (!inputValues) return [];
    const grouped: any[] = [];

    // find max index
    const indexes = new Set(
      Object.keys(inputValues).map((k) => k.split("-")[0])
    );

    indexes.forEach((idx) => {
      grouped.push({
        title: inputValues[`${idx}-title`],
        description: inputValues[`${idx}-description`],
        format: inputValues[`${idx}-format`],
        order: inputValues[`${idx}-order`],
        value: inputValues[`${idx}-value_for_test`],
        type: inputValues[`${idx}-type`],
        key: inputValues[`${idx}-key`],
      });
    });

    // sort by order
    return grouped.sort((a, b) => a.order - b.order);
  }, [inputValues]);

  const FieldCard = ({
    item,
    index,
    isOutput = false,
  }: {
    item: any;
    index: number;
    isOutput?: boolean;
  }) => (
    <Grid item xs={12}>
      <Accordion
        elevation={0}
        disableGutters
        sx={{
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 3,
        }}
      >
        {/* Accordion Header */}
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              justifyContent: "space-between",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
            <Typography
              component="span"
              sx={{
                width: "33%",
                flexShrink: 0,
                textTransform: "capitalize",
              }}
            >
              {item?.key || "Unnamed Field"}
            </Typography>
            <Typography component="span" sx={{ color: "text.secondary" }}>
              {isOutput ? `Output ${index + 1}` : `Input ${index + 1}`}
            </Typography>
          </Box>
        </AccordionSummary>

        {/* Accordion Body */}
        <AccordionDetails sx={{ borderTop: "1px solid rgba(0,0,0,0.12)" }}>
          <Stack flexDirection={"row"} justifyContent={"end"}>
            <FormControlLabel
              disabled
              control={<Checkbox color="secondary" />}
              label="UNIQ"
            />
            <FormControlLabel
              disabled
              control={<Checkbox color="secondary" />}
              label="Not Mutable"
            />
          </Stack>
          <Grid container spacing={2}>
            {/* Description */}
            {item.description && (
              <Grid item xs={12}>
                <FormLabel sx={{ fontSize: 12, color: "text.secondary" }}>
                  Description
                </FormLabel>
                <Typography>{item.description}</Typography>
              </Grid>
            )}

            {/* Type */}
            {item.type && (
              <Grid item xs={3}>
                <FormLabel sx={{ fontSize: 12, color: "text.secondary" }}>
                  Type
                </FormLabel>
                <Typography>{item.type}</Typography>
              </Grid>
            )}

            {/* Format */}
            {!isOutput && item.format && (
              <Grid item xs={3}>
                <FormLabel sx={{ fontSize: 12, color: "text.secondary" }}>
                  Format
                </FormLabel>
                <Typography variant="body2">{item.format}</Typography>
              </Grid>
            )}

            {/* Order */}
            {item.order && (
              <Grid item xs={3}>
                <FormLabel sx={{ fontSize: 12, color: "text.secondary" }}>
                  Order
                </FormLabel>
                <Typography variant="body2">{item.order}</Typography>
              </Grid>
            )}

            {/* Default Value */}
            {item.defaultValue && (
              <Grid item xs={3}>
                <FormLabel sx={{ fontSize: 12, color: "text.secondary" }}>
                  Default Value
                </FormLabel>
                <Typography variant="body2">{item.defaultValue}</Typography>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );

  const SectionHeader = ({
    title,
    // count,
    icon,
  }: {
    title: string;
    count: number;
    icon: string;
  }) => (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 400,
            fontSize: 16,
            color: "text.primary",
          }}
        >
          {icon} {title}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Grid container spacing={2} sx={{ mt: 0 }}>
      {/* Input Fields Section */}
      <Grid item xs={6} display="flex" flexDirection="column">
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
        ></Grid>
        <SectionHeader
          title="Input Fields"
          count={structuredInputs.length}
          icon=""
        />
        {structuredInputs.length > 0 ? (
          <Grid
            container
            sx={{
              maxHeight: 300, // max height 50px
              overflowY: "auto", // scroll vertically if content exceeds
              pr: 1,
            }}
          >
            {structuredInputs.map((item: any, index: number) => (
              <FieldCard key={index} item={item} index={index} />
            ))}
          </Grid>
        ) : (
          <Paper
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: "grey.100",
              border: "2px dashed",
              borderColor: "grey.300",
              borderRadius: 3,
            }}
          >
            <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
              No input fields configured
            </Typography>
          </Paper>
        )}
      </Grid>

      {/* Output Fields Section */}
      <Grid item xs={6}>
        <SectionHeader
          title="Output Fields"
          count={output ? Object.keys(output.properties || {}).length : 0}
          icon=""
        />

        {output && output.properties ? (
          <Grid
            container
            sx={{
              maxHeight: 300, // max height 50px
              overflowY: "auto", // scroll vertically if content exceeds
              pr: 1,
            }}
          >
            {Object.entries(output.properties)
              .sort(([, a]: [string, any], [, b]: [string, any]) => {
                const orderA = a.order || 0;
                const orderB = b.order || 0;
                return orderA - orderB;
              })
              .map(([key, item]: [string, any], index: number) => (
                <FieldCard
                  key={key}
                  item={{ ...item, key }}
                  index={index}
                  isOutput={true}
                />
              ))}
          </Grid>
        ) : (
          <Paper
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: "grey.100",
              border: "2px dashed",
              borderColor: "grey.300",
              borderRadius: 3,
            }}
          >
            <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
              No output fields configured
            </Typography>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export default DataStoreRequestRunQueryInputOutputFields;
